// Cart component for managing items and checkout
import React, { useEffect, useState, useMemo } from 'react';
import { useCartStore } from '../store/cartStore';
import { useStocksStore } from '../store/stocksStore';
import { useDiscount, useOrderManagement, useMinimumOrder } from '../hooks';
import { storageService } from '../services/storage';
import { collectClientMetadata, getVisitorIdentitySnapshot } from '../services/identity';
import { sendHeadsup } from '../services/api';
import { OrderFormData, PaymentRedirect, SelectedOption, Product } from '../types';
import CheckoutForm from './CheckoutForm';
import './Cart.css';
import { roundCurrency, calculateDisplayPrice } from '../shared/pricing';
import PaymentView from './PaymentView';
import OrderSuccess from './OrderSuccess';
import { AllergenDisplay } from './AllergenDisplay';
import { calculateProductAllergens } from '../worker/allergens';
import { AppliedDiscount } from '../types';
import { ProductOptionsModal } from './ProductOptionsModal';
import { TEMPORARY_CLOSURE_ENABLED, TEMPORARY_CLOSURE_ORDER_ERROR } from '../shared/temporaryClosure';

/**
 * Format discount description for display
 * Shows "£X off + Y% off" when both fixed and percentage exist
 */
const formatDiscountDescription = (discount: AppliedDiscount | null | undefined): string => {
    if (!discount) return '';

    const parts: string[] = [];

    if (discount.fixedAmount && discount.fixedAmount > 0) {
        parts.push(`£${discount.fixedAmount.toFixed(2)} off`);
    }

    if (discount.percentage && discount.percentage > 0) {
        parts.push(`${discount.percentage}% off`);
    }

    return parts.join(' + ') || 'discount applied';
};

interface CartProps {
    className?: string;
    variant?: 'inline' | 'modal';
    initialView?: CartView;
    checkoutMode?: 'inline' | 'external';
    onRequestCheckout?: () => void;
    onClose?: () => void;
    titleId?: string;
}

type CartView = 'cart' | 'checkout' | 'payment' | 'success';

const Cart: React.FC<CartProps> = ({
    className = '',
    variant = 'inline',
    initialView = 'cart',
    checkoutMode = 'inline',
    onRequestCheckout,
    onClose
}) => {
    const {
        cart,
        removeFromCart,
        updateQuantity,
        updateCartItem,
        clearCart,
        customer,
        setCustomer
    } = useCartStore();
    const {
        applyCode,
        removeCode,
        isApplying,
        status: discountStatus,
        message: discountMessage,
        error: discountError,
        discount
    } = useDiscount();

    const { submitOrder, isSubmitting, submitError } = useOrderManagement();
    const { meetsMinimum, minimumText } = useMinimumOrder();
    const [view, setView] = useState<CartView>(TEMPORARY_CLOSURE_ENABLED && initialView === 'checkout' ? 'cart' : initialView);
    const [paymentRedirect, setPaymentRedirect] = useState<PaymentRedirect | null>(null);
    const [lastSubmittedOrder, setLastSubmittedOrder] = useState<{ order: any; customer: any } | null>(null);
    const isModalVariant = variant === 'modal';
    const [discountInput, setDiscountInput] = useState<string>(discount?.code ?? '');
    const [showDiscountForm, setShowDiscountForm] = useState<boolean>(false);
    const hasDiscount = Boolean(discount?.code);
    const { stocks, fetchStocks } = useStocksStore();

    // Edit cart item state
    const [editingItemIndex, setEditingItemIndex] = useState<number | null>(null);
    const [editingProduct, setEditingProduct] = useState<Product | null>(null);
    const [editingOptions, setEditingOptions] = useState<SelectedOption[]>([]);
    const [editingQuantity, setEditingQuantity] = useState<number>(1);

    // Fetch stocks on mount if not already loaded
    useEffect(() => {
        fetchStocks();
    }, [fetchStocks]);

    // Calculate allergens for each cart item
    const itemsWithAllergens = useMemo(() => {
        return cart.items.map(item => ({
            ...item,
            allergenSummary: calculateProductAllergens(item.product, stocks || [], item.selectedOptions)
        }));
    }, [cart.items, stocks]);

    useEffect(() => {
        setDiscountInput(discount?.code ?? '');
        if (discount?.code) {
            setShowDiscountForm(false);
        }
    }, [discount?.code]);

    useEffect(() => {
        setView(TEMPORARY_CLOSURE_ENABLED && initialView === 'checkout' ? 'cart' : initialView);
        setPaymentRedirect(null);
    }, [initialView]);

    // useEffect(() => {
    //     if (cart.items.length === 0 && view === 'checkout') {
    //         setView('cart');
    //     }
    // }, [cart.items.length, view]);

    const handleQuantityChange = (cartItemIndex: number, newQuantity: number) => {
        if (newQuantity <= 0) {
            removeFromCart(cartItemIndex);
        } else {
            updateQuantity(cartItemIndex, newQuantity);
        }
    };

    const handleEditItem = (index: number) => {
        const item = cart.items[index];
        if (!item) return;

        setEditingItemIndex(index);
        setEditingProduct(item.product);
        setEditingOptions(item.selectedOptions);
        setEditingQuantity(item.quantity);
    };

    const handleCloseEditModal = () => {
        setEditingItemIndex(null);
        setEditingProduct(null);
        setEditingOptions([]);
        setEditingQuantity(1);
    };

    const handleUpdateCartItem = (selectedOptions: SelectedOption[], quantity: number) => {
        if (editingItemIndex !== null) {
            updateCartItem(editingItemIndex, selectedOptions, quantity);
        }
        handleCloseEditModal();
    };

    const handleStartCheckout = () => {
        if (TEMPORARY_CLOSURE_ENABLED) {
            return;
        }

        if (cart.items.length === 0) {
            return;
        }

        // Send headsup notification for checkout launched
        try {
            const visitorIdentity = getVisitorIdentitySnapshot();
            const savedCustomer = storageService.getCustomer();
            const hasUserInfo = !!(savedCustomer?.name || savedCustomer?.address);

            sendHeadsup({
                eventType: 'checkout_launched',
                visitorId: visitorIdentity?.id,
                customerName: savedCustomer?.name || undefined,
                customerAddress: savedCustomer?.address || undefined,
                cartItems: cart.items,
                // Only include visit count if user doesn't have previous order info
                visitCount: !hasUserInfo ? visitorIdentity?.visitCount : undefined,
            });
        } catch (error) {
            // Silently ignore - headsup is non-critical
            console.log('[Headsup] Error sending checkout headsup, continuing...', error);
        }

        setPaymentRedirect(null);

        if (checkoutMode === 'external') {
            onRequestCheckout?.();
            return;
        }

        setView('checkout');
    };

    const handleCheckout = async (orderData: OrderFormData) => {
        try {
            const driverTipAmount = Number(orderData.driverTipAmount ?? 0);
            const normalizedDriverTipAmount = Number.isFinite(driverTipAmount) && driverTipAmount > 0
                ? Number(driverTipAmount.toFixed(2))
                : 0;

            const subtotalBeforeDiscount = roundCurrency(cart.totals.subtotal);
            const discountAmount = roundCurrency(cart.totals.discountAmount);
            const basketTotal = roundCurrency(cart.totals.total);
            const orderTotal = roundCurrency(basketTotal + normalizedDriverTipAmount);

            const clientMetadata = collectClientMetadata();

            const order = {
                customerName: orderData.customer.name,
                customerEmail: orderData.customer.email,
                customerPhone: orderData.customer.phone,
                address: orderData.customer.address,
                // New structured address fields
                postcode: orderData.customer.postcode || '',
                number: orderData.customer.number || '',
                street: orderData.customer.street || '',
                area: orderData.customer.area || '',
                isPostcodeFound: orderData.customer.isPostcodeFound,
                items: cart.items.map(item => ({
                    productId: item.product.id,
                    name: item.product.name,
                    quantity: item.quantity,
                    price: item.totalPrice,
                    selectedOptions: item.selectedOptions.map(selectedOption => {
                        const option = item.product.options.find(opt => opt.id === selectedOption.optionId);
                        const selectedChoices = selectedOption.choiceIds
                            .map(choiceId => option?.choices.find(ch => ch.id === choiceId))
                            .filter(choice => choice !== undefined);

                        return {
                            optionId: selectedOption.optionId,
                            optionName: option?.name || `Option ${selectedOption.optionId}`,
                            choiceIds: selectedOption.choiceIds,
                            choiceNames: selectedChoices.map(choice => choice?.name ?? '')
                        };
                    }),
                })),
                subtotal: subtotalBeforeDiscount,
                discountCode: cart.discount?.code ?? null,
                discountPercentage: cart.discount?.percentage ?? null,
                discountAmount,
                total: orderTotal,
                deliveryDate: orderData.deliveryDate,
                deliveryTime: orderData.deliveryTime,
                deliverNow: orderData.deliverNow,
                deliveryNotes: orderData.deliveryNotes,
                paymentMethod: orderData.paymentMethod,
                driverTipAdded: !!orderData.driverTipAdded && normalizedDriverTipAmount > 0,
                driverTipAmount: normalizedDriverTipAmount,
                receiveEmailUpdates: !!orderData.customer.receiveEmailUpdates,
                clientMetadata: clientMetadata ?? undefined,
            };

            const submittedOrder = await submitOrder(order) as any;

            const redirectInfo = submittedOrder.paymentRedirect as PaymentRedirect | undefined;

            // Save customer info (excluding receiveEmailUpdates - we always default it to true)
            const { receiveEmailUpdates: _ignored, ...customerToSave } = orderData.customer;
            setCustomer(customerToSave as typeof orderData.customer);
            storageService.addToOrderHistory(submittedOrder);

            // Send order placed summary to headsup channel (only stock items, name, address)
            try {
                sendHeadsup({
                    eventType: 'order_placed_summary',
                    customerName: orderData.customer.name || undefined,
                    customerAddress: orderData.customer.address || undefined,
                    cartItems: cart.items,
                });
            } catch (headsupError) {
                // Silently ignore - headsup is non-critical
                console.log('[Headsup] Error sending order placed summary, continuing...', headsupError);
            }

            // Store order data for success view
            setLastSubmittedOrder({
                order: submittedOrder,
                customer: orderData.customer
            });

            if (redirectInfo?.redirectToPayment) {
                setPaymentRedirect(redirectInfo);
                setView('payment');
            } else {
                setPaymentRedirect(null);
                clearCart();
                setView('success');
            }

        } catch (error) {
            // Error is handled by the hook
            console.error('Order submission failed:', error);
        }
    };

    const handleCancelCheckout = () => {
        setPaymentRedirect(null);
        setView('cart');
    };

    const handleModalClose = () => {
        setPaymentRedirect(null);
        setView('cart');
        onClose?.();
    };

    const handlePaymentComplete = () => {
        setPaymentRedirect(null);
        clearCart();
        setView('success');
    };

    const renderCartItems = () => (
        <>
            {cart.items.length === 0 ? (
                <div className="empty-cart">
                    <div className="empty-icon">🛒</div>
                    <p>Your basket is empty</p>
                    <p className="empty-subtitle">Add some delicious items to get started!</p>
                </div>
            ) : (
                <>
                    <div className="cart-items">
                        {cart.items.map((item, index) => (
                            <div key={`${item.product.id}-${index}`} className="cart-item">
                                {/* First row: Image and Title */}
                                <div className="item-header">
                                    <img
                                        src={item.product.image}
                                        alt={item.product.name}
                                        className="item-image"
                                    />
                                    <h4 className="item-title">{item.product.name}</h4>
                                </div>

                                {/* Second row: Options */}
                                {item.selectedOptions.length > 0 && (
                                    <div className="item-options">
                                        {item.selectedOptions.map(selectedOption => {
                                            const option = item.product.options.find(opt => opt.id === selectedOption.optionId);
                                            if (!option) return null;

                                            const selectedChoices = selectedOption.choiceIds
                                                .map(choiceId => option.choices.find(ch => ch.id === choiceId))
                                                .filter(choice => choice !== undefined);

                                            return (
                                                <div key={selectedOption.optionId} className="option-detail">
                                                    <span className="option-name">{option.name}:</span>
                                                    <span className="option-value">
                                                        {selectedChoices.map(choice => choice?.name).join(', ')}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                {/* Third row: Price */}
                                <div className="item-price-row">
                                    {cart.discount && cart.discount.percentage > 0 ? (
                                        <>
                                            <p className="item-price original">£{item.totalPrice.toFixed(2)}</p>
                                            <p className="item-price">
                                                £{calculateDisplayPrice(item.totalPrice, cart.discount.percentage).currentPrice.toFixed(2)}
                                            </p>
                                        </>
                                    ) : (
                                        <p className="item-price">£{item.totalPrice.toFixed(2)}</p>
                                    )}
                                </div>

                                {/* Fourth row: Controls */}
                                <div className="item-controls">
                                    <div className="quantity-controls">
                                        <button
                                            className="qty-btn"
                                            onClick={() => handleQuantityChange(index, item.quantity - 1)}
                                        >
                                            −
                                        </button>
                                        <span className="quantity">{item.quantity}</span>
                                        <button
                                            className="qty-btn"
                                            onClick={() => handleQuantityChange(index, item.quantity + 1)}
                                        >
                                            +
                                        </button>
                                    </div>

                                    <div className="item-action-buttons">
                                        <button
                                            className="edit-btn"
                                            onClick={() => handleEditItem(index)}
                                        >
                                            Edit
                                        </button>
                                        <button
                                            className="remove-btn"
                                            onClick={() => removeFromCart(index)}
                                        >
                                            🗑️
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="cart-footer">
                        <div className="cart-totals">
                            <div className="totals-row">
                                <span>Subtotal</span>
                                <span>£{cart.totals.subtotal.toFixed(2)}</span>
                            </div>
                            {cart.totals.discountAmount > 0 && cart.discount?.code && (
                                <div className={`totals-row discount ${cart.discount?.isStale ? 'stale' : ''}`}>
                                    <span>{cart.discount.isStale ? 'Discount (revalidation needed)' : `Discount (${cart.discount.code}) - ${formatDiscountDescription(cart.discount)}`}</span>
                                    <span>-£{cart.totals.discountAmount.toFixed(2)}</span>
                                </div>
                            )}
                            <div className="totals-row total">
                                <span>Total</span>
                                <span>£{cart.totals.total.toFixed(2)}</span>
                            </div>
                        </div>

                        {TEMPORARY_CLOSURE_ENABLED ? (
                            <div className="temporary-closure-cart-note" role="status">
                                <strong>Ordering is paused</strong>
                                <span>{TEMPORARY_CLOSURE_ORDER_ERROR}</span>
                            </div>
                        ) : (
                            <>
                                {/* Minimum Order Warning - uses centralized hook */}
                                {!meetsMinimum && minimumText && (
                                    <div className="minimum-order-warning">
                                        <span className="warning-icon">ℹ️</span>
                                        <span className="warning-text">
                                            Minimum basket amount is {minimumText} to place an order
                                        </span>
                                    </div>
                                )}
                                <div className="discount-section">
                                    {!hasDiscount && (
                                        <>
                                            <label className="discount-toggle">
                                                <input
                                                    type="checkbox"
                                                    checked={showDiscountForm}
                                                    onChange={(event) => {
                                                        const checked = event.target.checked;
                                                        setShowDiscountForm(checked);
                                                        if (!checked) {
                                                            removeCode();
                                                            setDiscountInput('');
                                                        }
                                                    }}
                                                    disabled={cart.items.length === 0}
                                                />
                                                <span>Have a promo code?</span>
                                            </label>

                                            {showDiscountForm && (
                                                <form
                                                    className="discount-form"
                                                    onSubmit={(event) => {
                                                        event.preventDefault();
                                                        const trimmed = discountInput.trim();
                                                        if (trimmed.length === 0) {
                                                            return;
                                                        }
                                                        void applyCode(trimmed);
                                                    }}
                                                >
                                                    <div className="discount-controls">
                                                        <input
                                                            id="discount-code"
                                                            name="discount-code"
                                                            type="text"
                                                            value={discountInput}
                                                            onChange={(event) => setDiscountInput(event.target.value.toUpperCase())}
                                                            placeholder="ENTER CODE"
                                                            autoComplete="off"
                                                            className="discount-input"
                                                            disabled={isApplying || cart.items.length === 0}
                                                        />
                                                        <button
                                                            type="submit"
                                                            className="discount-apply-btn"
                                                            disabled={isApplying || discountInput.trim().length === 0 || cart.items.length === 0}
                                                        >
                                                            {isApplying ? 'Applying…' : 'Apply'}
                                                        </button>
                                                    </div>
                                                </form>
                                            )}
                                        </>
                                    )}

                                    {hasDiscount && (
                                        <div className="discount-applied">
                                            <span className="discount-applied-message">
                                                Promo code <strong className="discount-applied-code">{discount?.code}</strong> ({formatDiscountDescription(discount)}) applied.
                                            </span>
                                            <button
                                                type="button"
                                                className="discount-remove-btn"
                                                onClick={() => {
                                                    removeCode();
                                                    setDiscountInput('');
                                                    setShowDiscountForm(false);
                                                }}
                                                disabled={isApplying}
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    )}

                                    {(discountStatus === 'validating' || discountError || discountMessage || discount?.isStale) && (
                                        <div className="discount-feedback" role="status" aria-live="polite">
                                            {discountStatus === 'validating' && (
                                                <p className="discount-message">Checking code…</p>
                                            )}
                                            {discountStatus !== 'validating' && discountError && (
                                                <p className="discount-error">{discountError}</p>
                                            )}
                                            {discountStatus !== 'validating' && !discountError && discountMessage && (
                                                <p className="discount-message">{discountMessage}</p>
                                            )}
                                            {discountStatus !== 'validating' && !discountError && !discountMessage && discount?.isStale && (
                                                <p className="discount-warning">Basket changed. We’ll recheck your code at checkout.</p>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <button
                                    className="checkout-btn"
                                    onClick={handleStartCheckout}
                                    disabled={cart.items.length === 0 || !meetsMinimum}
                                >
                                    Checkout
                                </button>
                            </>
                        )}
                    </div>
                </>
            )}
        </>
    );

    const renderContent = () => {
        switch (view) {
            case 'success':
                return (
                    <OrderSuccess
                        order={lastSubmittedOrder?.order}
                        customer={lastSubmittedOrder?.customer}
                        customerName={lastSubmittedOrder?.customer?.name}
                        customerAddress={lastSubmittedOrder?.customer?.address}
                        customerPhone={lastSubmittedOrder?.customer?.phone}
                    />
                );
            case 'checkout':
                return (
                    <CheckoutForm
                        onSubmit={handleCheckout}
                        onCancel={handleCancelCheckout}
                        isSubmitting={isSubmitting}
                        error={submitError}
                        initialData={customer}
                    />
                );
            case 'payment':
                return (
                    <PaymentView
                        paymentRedirect={paymentRedirect}
                        onPaymentComplete={handlePaymentComplete}
                    />
                );
            case 'cart':
            default:
                return renderCartItems();
        }
    };

    const rootClassName = [
        'cart-container',
        variant === 'modal' ? 'cart-modal' : 'cart-inline',
        className
    ].filter(Boolean).join(' ');

    return (
        <div className={rootClassName} id={variant === 'inline' ? 'cart-section' : undefined}>
            {isModalVariant && (
                <button
                    className="cart-modal-close"
                    onClick={handleModalClose}
                    type="button"
                    aria-label="Close basket"
                >
                    ×
                </button>
            )}
            <div className="cart-content">
                {renderContent()}
            </div>

            {/* Edit Item Modal */}
            {editingProduct && (
                <ProductOptionsModal
                    product={editingProduct}
                    isOpen={editingItemIndex !== null}
                    onClose={handleCloseEditModal}
                    onAddToCart={handleUpdateCartItem}
                    editMode={true}
                    initialSelectedOptions={editingOptions}
                    initialQuantity={editingQuantity}
                    highZIndex={isModalVariant}
                />
            )}
        </div>
    );
};

export default Cart;
